import InvoicingData from 'components/pages/OrderPage/components/InvoicingData';
import OrdersTable from 'components/pages/OrderPage/components/OrdersTable';
import OrderSummary from 'components/pages/OrderPage/components/OrderSummary';
import useOrderPage from 'components/pages/OrderPage/useOrderPage';
import useOrderStatusPage from 'components/pages/OrderStatusPage/useOrderStatusPage';
import Button from 'components/ui/Button/Button';
import Logo from 'components/ui/Logo/Logo';
import Logos from 'components/ui/Logos/Logos';
import PageSettings from 'components/ui/PageSettings/PageSettings';
import StepIndicator from 'components/ui/StepIndicator/StepIndicator';
import { useMemo } from 'react';

const OrderStatusPage = () => {
  const { orderData, handleGoBack } = useOrderPage();
  const {
    simpleResponse,
    goToOrderPage,
    status,
    isOrderDataLoading,
    goToMerchantPage,
    goToTap
  } = useOrderStatusPage();

  const indicatorStatus = useMemo(() => {
    if (simpleResponse.e === 'DIJB' || status === 'FINISHED') return 'DONE';
    if (status === 'AUTHORIZED') return undefined;
    if (!status && simpleResponse.e === 'SUCCESS') {
      return undefined;
    }
    return 'ERROR';
  }, [status, simpleResponse]);

  if (!orderData) {
    return <div>Order not found</div>;
  }

  // after payment, before IPN response
  const getPreContent = () => {
    // 2023-10-05 pjung PARWEB-10 Díjbekérő
    if (simpleResponse.e === 'DIJB')
      return (
        <div>
          <PageSettings issuer={orderData.issuer} page="Átutalásos megrendelés" />
          <h2 className="text-2xl lg:text-3xl font-bold text-center my-8">
            Köszönjük megrendelését!
          </h2>
          <h2 className="text-xl mb-4 text-center">
            Kérjük szíves türelmét, kollégáink a megadott adatok alapján kiállítják, és a
            jelzett e-mail címre elküldik a díjbekérőt a kiegyenlítéshez.
          </h2>
          <h2 className="text-xl font-bold mb-4 text-center">
            Kérjük a díjbekérőre történő átutalásos kiegyenlítésnél az{' '}
            <b>átutalás közlemény rovatába csak a díjbekérő sorszámát írja</b>, a könnyebb
            és gyorsabb beazonosítás végett!
          </h2>
          <div className={`mt-8 flex`}>
            <Button
              color="lightBlue"
              variant="default"
              onClick={() =>
                orderData.issuer === 'tapertek' ? goToTap() : goToMerchantPage()
              }
              disabled={isOrderDataLoading}
              fullWidth
              className="max-w-md mx-auto"
            >
              {orderData.issuer === 'tapertek' ? 'Befejezés' : 'Vissza a webáruházba'}
            </Button>
          </div>
        </div>
      );
    // Sikeres fizetés
    if (simpleResponse.e === 'SUCCESS')
      return (
        <div className="flex flex-col items-center">
          <PageSettings issuer={orderData.issuer} page="Sikeres fizetés" />
          <div role="status" className="my-4 text-center">
            <svg
              aria-hidden="true"
              className="mr-2 w-8 h-8 text-gray-200 animate-spin fill-lightBlue-100"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
          <div>
            <h2 className="text-2xl lg:text-3xl text-center font-bold my-8">
              A fizetés feldolgozását megkezdtük
            </h2>
            <h2 className="text-xl text-center font-bold">
              Kérjük ne zárja be az oldalt!
            </h2>
            <h2 className="text-xl text-center font-bold mb-4">
              SimplePay tranzakció azonosító: {simpleResponse.t}
            </h2>
          </div>
        </div>
      );
    // Visszalépés
    if (simpleResponse.e === 'CANCEL')
      return (
        <div>
          <PageSettings issuer={orderData.issuer} page="Megszakított fizetés" />
          <h2 className="text-2xl lg:text-3xl text-center my-8 font-semibold">
            Megszakított fizetés a Simplepay oldalán.
          </h2>
          <p className="text-xl font-normal">Megrendelését nem tudtuk fogadni.</p>
          <div className="mt-8">
            <Button
              color="lightBlue"
              variant="default"
              fullWidth
              className="max-w-md mx-auto"
              onClick={goToOrderPage}
            >
              Újra
            </Button>
          </div>
        </div>
      );
    // Időtúllépés
    if (simpleResponse.e === 'TIMEOUT')
      return (
        <div>
          <PageSettings issuer={orderData.issuer} page="Időtúllépés" />
          <h2 className="text-2xl lg:text-3xl text-center my-8 font-semibold">
            Ön túllépte a tranzakció elindításának lehetséges maximális idejét a Simplepay
            oldalon, így rendelését nem tudtuk fogadni.
          </h2>
          <h2 className="text-xl font-bold mb-4 text-center">
            SimplePay tranzakció azonosító: {simpleResponse.t}
          </h2>
          <div className="mt-8 w-full">
            <Button
              color="lightBlue"
              variant="default"
              fullWidth
              className="max-w-md mx-auto"
              onClick={goToOrderPage}
            >
              Újra
            </Button>
          </div>
        </div>
      );
    // Egyéb sikertelenség
    return (
      <div>
        <PageSettings issuer={orderData.issuer} page="Sikertelen fizetés" />
        <h2 className="text-2xl lg:text-3xl text-center my-8 font-semibold">
          A fizetés sikertelen.
        </h2>
        <h2 className="text-xl font-bold mb-4 text-center">
          SimplePay tranzakció azonosító: {simpleResponse.t}
        </h2>
        <p className="text-xl font-normal">
          Kérjük, ellenőrizze a tranzakció során megadott adatok helyességét! Amennyiben
          minden adatot helyesen adott meg, a visszautasítás okának kivizsgálása érdekében
          kérjük, szíveskedjen kapcsolatba lépni kártyakibocsátó bankjával.
        </p>
        <div className="mt-8">
          <Button
            color="lightBlue"
            variant="default"
            fullWidth
            className="max-w-md mx-auto"
            onClick={goToOrderPage}
          >
            Újra
          </Button>
        </div>
      </div>
    );
  };

  // after IPN response
  const getAfterContent = () => {
    let btnText;
    if (orderData.issuer === 'arvaltozas') btnText = 'Bejelentkezés';
    else if (orderData.issuer === 'tapertek') btnText = 'Befejezés';
    else btnText = 'Vissza a webáruházba';

    switch (status) {
      // Sikeres fizetés
      case 'FINISHED':
        return (
          <div>
            <PageSettings issuer={orderData.issuer} page="Fizetés visszaigazolás" />
            <h2 className="text-2xl lg:text-3xl font-bold text-center my-8">
              Köszönjük a megrendelést!
            </h2>
            <h2 className="text-xl font-bold mb-4 text-center">
              A tranzakció sikeresen megtörtént.
            </h2>
            <h2 className="text-xl font-bold mb-4 text-center">
              SimplePay tranzakció azonosító: {simpleResponse.t}
            </h2>
            <div className="opacity-40">
              <section className="pb-4 mb-4">
                <InvoicingData invoicing={orderData.invoicing} />
                <div className="w-full mt-6 border-b-darkBlue-5 border" />
              </section>
              <section className="pb-4 mb-4">
                <OrdersTable orderData={orderData} />
              </section>
              <OrderSummary orderData={orderData} />
              <section>
                <div className="mt-8">
                  <Logos url="https://simplepartner.hu/PaymentService/Fizetesi_tajekoztato.pdf" />
                </div>
              </section>
            </div>
            <div className={`mt-8 flex`}>
              <Button
                color="lightBlue"
                variant="default"
                onClick={() =>
                  orderData.issuer === 'tapertek' ? goToTap() : goToMerchantPage()
                }
                disabled={isOrderDataLoading}
                fullWidth
                className="max-w-md mx-auto"
              >
                {btnText}
              </Button>
            </div>
          </div>
        );
      // Ismeretlen
      case undefined:
        return null;
      // minden más...
      default:
        return (
          <div>
            <PageSettings issuer={orderData.issuer} page="Sikertelen fizetés" />
            <h2 className="text-2xl lg:text-3xl font-bold text-center my-8">
              A tranzakció sikertelen, kérlek próbáld újra!
            </h2>
            <div className="opacity-40">
              <section className="pb-4 mb-4">
                <InvoicingData invoicing={orderData.invoicing} />
                <div className="w-full mt-6 border-b-darkBlue-5 border" />
              </section>
              <section className="pb-4 mb-4">
                <OrdersTable orderData={orderData} />
              </section>
              <OrderSummary orderData={orderData} />
              <section className="pb-4 mb-4">
                <div className="mt-8">
                  <Logos url="https://simplepartner.hu/PaymentService/Fizetesi_tajekoztato.pdf" />
                </div>
              </section>
            </div>
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                color="lightBlue"
                className="mr-4"
                onClick={handleGoBack}
              >
                Vissza
              </Button>
              <Button
                variant="default"
                color="lightBlue"
                className="ml-4"
                onClick={goToOrderPage}
                fullWidth
              >
                Újra
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="w-full p-8 text-darkBlue-100">
      <div className="flex justify-center mb-8">
        <div className="w-56">
          <Logo issuer={orderData.issuer} />
        </div>
      </div>
      <StepIndicator stepCount={3} currentStep={3} status={indicatorStatus} />
      {!status ? getPreContent() : getAfterContent()}
    </div>
  );
};

export default OrderStatusPage;
